import React, { useEffect, useRef, useState } from "react";
import { Spin, Table } from "antd";
import Image from "next/image";
import type { IRes } from "@aspen/model";
import { message, WithEmptyContent } from "@aspen/ui";
import { downLoadFileBlob, getFileMimeType, i18nUtil } from "@aspen/libs";
import dayjs from "dayjs";

import noResultsImg from "@aspen/assets/images/no_results.png";
import { getExportHistoryList, downloadHistory } from "@aspen/services";

interface IDocHistoryItem {
  id: string;
  fileName: string;
  url: string | null;
  created: string;
  period?: string;
  status?: string;
}

const GeneratedHistoryTab: React.FC = () => {
  const intl = i18nUtil.t();

  const [loading, setLoading] = useState<boolean>(false);
  const [documentList, setDocumentList] = useState<IDocHistoryItem[]>([]);
  const nextTokenRef = useRef<string>("");

  // 获取文档历史记录
  const fetchExportHistoryList = (
    params: { limit: number; nextToken?: string } = { limit: 12 },
    loadMore = false,
  ) => {
    setLoading(true);
    getExportHistoryList(params)
      .then((res: IRes) => {
        if (res?.code === "0") {
          const { data } = res;
          const { rows = [], nextToken } = data;
          nextTokenRef.current = nextToken;
          setDocumentList(prev => (loadMore ? prev.concat(rows) : rows));
        } else {
          message.error(intl?.[res?.msg] ?? res?.msg);
        }
      })
      .catch(err => {
        message.error(intl?.[err?.msg] ?? err?.msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExportHistoryList();
  }, []);

  const downloadExportHistory = (item: IDocHistoryItem) => {
    if (item.url) {
      downloadHistory({ id: item.id }).then(res => {
        downLoadFileBlob(res, getFileMimeType(item.url ?? ""), item.fileName);
      });
    }
  };

  const columns = [
    {
      title: intl["document.center.table.report"],
      dataIndex: "fileName",
      key: "fileName",
      render: (text: string) => text?.replace(/\.csv$/i, "") || text,
      width: 200,
      ellipsis: true,
      align: "left" as const,
    },
    {
      title: intl["document.center.table.period"],
      dataIndex: "period",
      key: "period",
      width: 200,
      ellipsis: true,
      align: "left" as const,
    },
    {
      title: intl["document.center.table.exportTime"],
      dataIndex: "created",
      key: "created",
      render: (createdTime: string) => dayjs(createdTime).format("YYYY-MM-DD HH:mm:ss"),
      width: 200,
      ellipsis: true,
      align: "left" as const,
    },
    {
      title: "",
      key: "action",
      render: (_text: string, record: IDocHistoryItem) => {
        const isGenerating = !record.url || record.status !== "SUCCESS";

        if (isGenerating) {
          return (
            <span className="font-common-regular text-sm leading-[22px] text-[#f17e00] whitespace-nowrap">
              {intl["document.center.status.generating"]}
            </span>
          );
        }

        return (
          <div
            onClick={() => downloadExportHistory(record)}
            className="font-common-regular text-sm leading-[22px] !text-[#f17e00] whitespace-nowrap underline underline-offset-2 cursor-pointer">
            {intl["button.download"]}
          </div>
        );
      },
      width: 120,
      align: "left" as const,
    },
  ];

  return (
    <div className="bg-[#262839] rounded-sm px-8 pt-4 pb-8 min-h-[600px] flex flex-col gap-8 items-center w-full max-w-[1200px] mx-auto">
      <WithEmptyContent showEmpty>
        <Spin spinning={loading}>
          {documentList.length ? (
            <Table columns={columns} dataSource={documentList} rowKey="id" pagination={false} />
          ) : (
            <div className="py-[100px] flex justify-center">
              <Image alt="" src={noResultsImg} width={240} height={180} />
            </div>
          )}
        </Spin>
      </WithEmptyContent>
      {nextTokenRef?.current ? (
        <div
          className="font-common-regular text-sm text-[#f17e00] cursor-pointer text-right no-underline hover:text-[#ff8c00]"
          onClick={() => {
            fetchExportHistoryList({ limit: 12, nextToken: nextTokenRef.current }, true);
          }}>
          {intl["button.loadMore"]}
        </div>
      ) : null}
    </div>
  );
};

export { GeneratedHistoryTab };
